import { NO_VALUE_PLACEHOLDER } from "@/const/default-values"
import {
  INVENTORY_PLATFORM_LABELS,
  getAgentPlatformKey,
  getInventoryPlatformKey,
  getRequiredAccess,
  isAgentSource
} from "@/domain/ai-inventory"
import { getIssuesCountMapOrderedSeverity } from "@/domain/vulnerability"
import {
  getApplications as getApplicationsFilter,
  getInputFilter,
  getYesNoOptions,
  helperKeyMultipleValues
} from "@/helpers/filters.helper"
import { isHttpsUrl } from "@/helpers/utils"
import { AGENT_AI_DISCOVERY_DRAWER_ID } from "@/organisms/Drawers/const"

import type {
  AgentAiApi,
  AgentAiByIdApi,
  AgentAiResponse,
  AgentPlatformInfoAiApi,
  AgentsAiResponse,
  CodeStepAiApi,
  EntitiesAi,
  KnowledgeSourcesAiResponse,
  McpClientsAiResponse,
  McpEndpointAiApi,
  McpLocalityAi,
  McpServerAiApi,
  McpServersAiResponse,
  ModelCallAiApi,
  ModelsAiResponse,
  RelatedApplicationsAiAPI,
  StatisticsAiResponse,
  ToolAiAPI,
  ToolsAiResponse,
  WorkflowAiByIdApi,
  WorkflowAiResponse,
  WorkflowDataFlowAiApi,
  WorkflowsAiResponse
} from "@/api/useInventory.types"
import type { InventoryPlatformAi } from "@/domain/ai-inventory"
import type { TFunction } from "@/organisms/Form/components/ResourcesList/ResourcesList.types"
import type {
  HelperFilter,
  OptionFilter
} from "@/organisms/SmartSearch/types/options.types"
import { CommonProps } from "./useAiDiscovery.types"

export const PREFIX_BY_TYPE: Record<EntitiesAi, string> = {
  agent: "a~",
  workflow: "w~",
  tool: "t~",
  model: "m~",
  mcp_server: "s~",
  knowledge_source: "k~"
} as const

export const MCP_LOCALITY_LABELS: Record<McpLocalityAi, string> = {
  local: "Local",
  remote: "Remote"
}

export const getKey = (key: string, prefix?: string) =>
  prefix ? `${prefix}${key}` : key

export const getFilterOptions = (
  filter: OptionFilter | OptionFilter[] | undefined
) => (!filter || Array.isArray(filter) ? [] : filter.options)

export const getApplications = (applications: RelatedApplicationsAiAPI) => ({
  copyable: true,
  title: "Applications",
  items: (applications || []).map(({ name, environment, namespace }) => ({
    name,
    description: `${environment || "-"}/${namespace || "-"}`
  })),
  count: applications?.length > 1 ? applications.length - 1 : 0,
  label: applications?.[0]?.name || ""
})

// A platform agent the platform hosts has its scope as its project, one
// without resources; an agent found in the code of one it runs keeps its own
const getPlatformScopeName = (
  key: InventoryPlatformAi | undefined,
  agent: Partial<AgentAiApi> | undefined
) =>
  key && agent?.project && !agent.project.resources.length
    ? agent.project.name
    : undefined

// The platform fields of an agent, flat or nested under its platform (the
// flat ones win); every field may be absent
export const getAgentPlatformDetails = (
  agent: Partial<AgentAiApi> | undefined,
  t: CommonProps["t"]
) => {
  const { platform } = agent || {}
  const nested: AgentPlatformInfoAiApi =
    platform && typeof platform === "object" ? platform : {}
  const key = getInventoryPlatformKey(
    typeof platform === "string" ? platform : nested.platform
  )
  const isPublished = agent?.isPublished ?? nested.isPublished
  const or = (value: string | undefined) => value || NO_VALUE_PLACEHOLDER
  // Inventory metadata: only an https link opens
  const consoleUrl = agent?.consoleUrl || nested.consoleUrl
  let published = NO_VALUE_PLACEHOLDER

  if (isPublished !== undefined)
    published = t(
      `inventory.ai.agents.${isPublished ? "published" : "unpublished"}`
    )

  return {
    platformKey: key,
    platformLabel: key ? INVENTORY_PLATFORM_LABELS[key] : NO_VALUE_PLACEHOLDER,
    owner: or(agent?.owner || nested.owner),
    published,
    authType: or(agent?.authType || nested.authType),
    consoleUrl: isHttpsUrl(consoleUrl) ? consoleUrl : undefined,
    resourceId: agent?.platformResourceId || nested.resourceId,
    scopeName: agent?.platformScopeName || getPlatformScopeName(key, agent)
  }
}

// Where a component comes from, as the Source column shows it. An agent
// platform hosts its components in a scope (an account and region, an
// environment), no code repository: inventory serves it as a project without
// resources, the experimental backend as the agent's platform scope
export const getProjectSource = (
  project: AgentAiApi["project"] | undefined,
  scopeName?: string
) => {
  const projectName = project?.name || scopeName || ""
  const resources = project?.resources.length
    ? project?.resources
    : [{ type: "AgentPlatform", name: projectName }]
  const { sourceControl, type } = resources[0] as {
    sourceControl?: string
    type: string
  }

  return { projectName, resources, sourceControl, type }
}

const getAgentAi = <T extends AgentAiApi>(agent: T) => {
  const { id, insights, project, models, ...restAgent } = agent
  const { projectName, resources, sourceControl, type } = getProjectSource(
    project,
    agent.platformScopeName
  )

  return {
    id,
    projectName,
    resources,
    key: id,
    insights: { id: `insight_${id}`, ...insights },
    modelShowMore: getShowMoreData(
      (models || []).map((model) => model.name),
      { id: `${id}_models` }
    ),
    severity: getIssuesCountMapOrderedSeverity(agent.riskSummary),
    apps: getApplications(agent.relatedApplications),
    sourceControl,
    type,
    ...restAgent
  }
}

type AgentAi<T extends AgentAiApi> = ReturnType<typeof getAgentAi<T>> & {
  platformDetails: ReturnType<typeof getAgentPlatformDetails>
}

// The list signature is last, since a selector passed by reference is inferred from it.
interface AgentsAiSelector {
  (response: AgentAiResponse): Omit<AgentAiResponse, "data"> & {
    data: AgentAi<AgentAiByIdApi>
  }
  (response: AgentsAiResponse): Omit<AgentsAiResponse, "data"> & {
    data: AgentAi<AgentAiApi>[]
  }
}

// Takes the agents' source filter options
// Only a cloud agent platform tells an agent's owner, published state and auth
// type; a Kubernetes cluster does not
export const hasPlatformAgents = (options: { value: string }[] | undefined) =>
  !!options?.some(({ value }) => getAgentPlatformKey(value))

// Takes the agents list or a single agent, so every caller passes the response it fetched.
export const getAgentsAi = ({ t }: CommonProps) =>
  ((response: AgentsAiResponse | AgentAiResponse) => {
    const { data, ...rest } = response
    const withPlatform = <T extends AgentAiApi>(agent: T) => ({
      ...getAgentAi(agent),
      platformDetails: getAgentPlatformDetails(agent, t)
    })

    return {
      ...rest,
      data:
        !data || Array.isArray(data)
          ? (data ?? []).map(withPlatform)
          : withPlatform(data)
    }
  }) as AgentsAiSelector

const getEnvironmentFilter = (
  environment: string[] | undefined,
  props: { prefix: string; t: TFunction }
) => ({
  key: getKey("environment", props.prefix),
  label: props.t("filters.environment"),
  type: "multiple" as const,
  options: (environment || []).map((env) => ({
    label: env,
    value: env
  })),
  helper: [
    {
      label: props.t(helperKeyMultipleValues, {
        label: props.t("filters.environment")
      }),
      description: props.t("filters.helpers.equals")
    }
  ] as [HelperFilter]
})

const getUsedByAgentFilter = (
  usedByAgent: string[] | undefined,
  { prefix, t }: { prefix: string; t: TFunction }
) => ({
  key: getKey("usedByAgent", prefix),
  label: t("filters.usedByAgent").toLowerCase(),
  displayLabel: t("filters.usedByAgent"),
  type: "multiple" as const,
  options: (usedByAgent || []).map((agent) => ({
    label: agent,
    value: agent
  })),
  helper: [
    {
      label: t(helperKeyMultipleValues, { label: t("filters.usedByAgent") }),
      description: t("filters.helpers.equals")
    }
  ] as [HelperFilter]
})

// The yes/no insights an agent and a workflow share
const getInsightsFilters = (prefix: string, t: TFunction) => {
  const single = "single" as const

  return [
    {
      key: getKey("runtime", prefix),
      label: t("filters.runtime"),
      type: single,
      options: getYesNoOptions(t)
    },
    {
      key: getKey("hasGuardrails", prefix),
      label: t("filters.hasGuardrails"),
      type: single,
      options: getYesNoOptions(t)
    },
    {
      key: getKey("isRoot", prefix),
      label: t("filters.isRoot").toLocaleLowerCase(),
      displayLabel: t("filters.isRoot"),
      type: single,
      options: getYesNoOptions(t)
    }
  ]
}

const getModelFilter = (
  models: string[] | undefined,
  { prefix, t }: { prefix: string; t: TFunction }
) => ({
  key: getKey("model", prefix),
  label: t("filters.model"),
  type: "multiple" as const,
  options: (models || []).map((model) => ({
    label: model,
    value: model
  })),
  helper: [
    {
      label: t(helperKeyMultipleValues, { label: t("filters.model") }),
      description: t("filters.helpers.equals")
    }
  ] as [HelperFilter]
})

const getSeverityFilter = (prefix: string, t: TFunction) => ({
  key: getKey("severity", prefix),
  label: t("filters.riskSeverity").toLocaleLowerCase(),
  displayLabel: t("filters.riskSeverity"),
  type: "multiple" as const,
  options: ["high", "medium", "low"].map((severity) => ({
    label: t(`vulnerabilities.${severity}`),
    value: severity
  })),
  helper: [
    {
      label: t(helperKeyMultipleValues, { label: t("filters.severity") }),
      description: t("filters.helpers.equals")
    }
  ] as [HelperFilter]
})

export const getAgentsAiFilters = (
  response: KosmosApi.Response<Record<string, string[]>>,
  props: { prefix: string; t: TFunction; isLive?: boolean }
): {
  [key in string]: OptionFilter | OptionFilter[]
} => {
  const { data } = response
  const { prefix, t, isLive } = props
  const insights = getInsightsFilters(prefix, t)
  const model = getModelFilter(data?.model, { prefix, t })
  const severity = getSeverityFilter(prefix, t)

  const source = {
    key: getKey("source", prefix),
    label: t("filters.source"),
    type: "multiple" as const,
    // Only the sources the tenant's agents come from: the platforms hosting
    // them, and the projects they were found in. A platform may come by any
    // of its names; the value goes back as it came
    options: (data?.source || []).flatMap((value) => {
      const platform = getInventoryPlatformKey(value)

      if (platform)
        return [{ label: INVENTORY_PLATFORM_LABELS[platform], value }]
      if (isAgentSource(value))
        return [{ label: t("inventory.ai.agents.sources.project"), value }]

      return []
    }),
    helper: [
      {
        label: t(helperKeyMultipleValues, { label: t("filters.source") }),
        description: t("filters.helpers.equals")
      }
    ] as [HelperFilter]
  }

  // A platform agent's owner, published state and auth type are filters rather than columns, offered
  // with the source: a tenant without platform agents is offered what it was before them. The
  // experimental backend keeps them as columns.
  const AGENT_COLUMNS = "inventory.ai.agents.columns"
  const owner = getInputFilter(getKey("owner", prefix), t, {
    label: `${AGENT_COLUMNS}.owner`
  })
  const published = {
    key: getKey("isPublished", prefix),
    label: t(`${AGENT_COLUMNS}.published`).toLocaleLowerCase(),
    displayLabel: t(`${AGENT_COLUMNS}.published`),
    type: "single" as const,
    options: (data?.isPublished || []).map((value) => ({
      label: t(
        `inventory.ai.agents.${value === "YES" ? "published" : "unpublished"}`
      ),
      value
    }))
  }
  const authType = {
    key: getKey("authType", prefix),
    label: t(`${AGENT_COLUMNS}.auth`),
    type: "multiple" as const,
    options: (data?.authType || []).map((value) => ({ label: value, value })),
    helper: [
      {
        label: t(helperKeyMultipleValues, {
          label: t(`${AGENT_COLUMNS}.auth`)
        }),
        description: t("filters.helpers.equals")
      }
    ] as [HelperFilter]
  }
  const sourceFilters = source.options.length
    ? [
        source,
        ...(isLive && hasPlatformAgents(source.options)
          ? [
              owner,
              ...(published.options.length ? [published] : []),
              ...(authType.options.length ? [authType] : [])
            ]
          : [])
      ]
    : []

  const { key: keyAppFilter, ...appFilter } = getApplicationsFilter(t)

  return {
    insights,
    model,
    severity,
    source,
    all: [
      getInputFilter(getKey("name", prefix), t, { label: "filters.agent" }),
      getInputFilter(getKey("codeRepository", prefix), t, {
        label: "filters.codeRepository"
      }),
      getInputFilter(getKey("projectResources", prefix), t, {
        label: "filters.project"
      }),
      model,
      severity,
      ...sourceFilters,
      {
        key: getKey(keyAppFilter, prefix),
        ...appFilter
      },
      getEnvironmentFilter(data?.environment, props),
      ...insights
    ]
  }
}

export const getWorkflowsAiFilters = (
  response: KosmosApi.Response<Record<string, string[]>>,
  props: { prefix: string; t: TFunction }
): {
  [key in string]: OptionFilter | OptionFilter[]
} => {
  const { data } = response
  const { prefix, t } = props
  const insights = getInsightsFilters(prefix, t)
  const severity = getSeverityFilter(prefix, t)
  const { key: keyAppFilter, ...appFilter } = getApplicationsFilter(t)

  return {
    insights,
    severity,
    all: [
      getInputFilter(getKey("name", prefix), t, { label: "filters.workflow" }),
      getInputFilter(getKey("projectResources", prefix), t, {
        label: "filters.project"
      }),
      severity,
      {
        key: getKey(keyAppFilter, prefix),
        ...appFilter
      },
      getEnvironmentFilter(data?.environment, props),
      ...insights
    ]
  }
}

export const getToolsAiFilters = (
  response: KosmosApi.Response<Record<string, string[]>>,
  props: { t: TFunction }
): {
  [key in string]: OptionFilter | OptionFilter[]
} => {
  const { data } = response
  const { t } = props
  const prefix = PREFIX_BY_TYPE.tool
  const type = {
    key: getKey("type", prefix),
    label: t("filters.type"),
    type: "multiple" as const,
    options: (data?.type || []).map((type) => ({
      label: type,
      value: type
    })),
    helper: [
      {
        label: t(helperKeyMultipleValues, { label: t("filters.type") }),
        description: t("filters.helpers.equals")
      }
    ] as [HelperFilter]
  }

  const dataDirection = {
    key: getKey("dataDirection", prefix),
    label: t("filters.dataDirection"),
    type: "multiple" as const,
    options: [
      { label: "Sink", value: "sink" },
      { label: "Source", value: "source" },
      { label: "Source and Sink", value: "source_sink" },
      { label: "Internal", value: "internal" }
    ],
    helper: [
      {
        label: t(helperKeyMultipleValues, {
          label: t("filters.dataDirection")
        }),
        description: t("filters.helpers.equals")
      }
    ] as [HelperFilter]
  }
  const dataCategories = {
    key: getKey("dataCategory", prefix),
    label: t("filters.dataCategory"),
    type: "multiple" as const,
    options: (data?.dataCategory || []).map((category) => ({
      label: t(`inventory.ai.categories.${category}`),
      value: category
    })),
    helper: [
      {
        label: t(helperKeyMultipleValues, {
          label: t("filters.dataCategories")
        }),
        description: t("filters.helpers.equals")
      }
    ] as [HelperFilter]
  }

  const usedByAgent = getUsedByAgentFilter(data?.usedByAgent, { prefix, t })

  return {
    type,
    usedByAgent,
    dataDirection,
    dataCategories,
    all: [
      getInputFilter(getKey("name", prefix), t, { label: "filters.tool" }),
      getEnvironmentFilter(data?.environment, { prefix, t }),
      type,
      usedByAgent,
      dataDirection,
      dataCategories
    ]
  }
}

export const getModelsAiFilters = (
  response: KosmosApi.Response<Record<string, string[]>>,
  props: { t: TFunction }
): {
  [key in string]: OptionFilter | OptionFilter[]
} => {
  const { data } = response
  const { t } = props
  const prefix = PREFIX_BY_TYPE.model
  const usedByAgent = getUsedByAgentFilter(data?.usedByAgent, { prefix, t })
  const vendor = {
    key: getKey("vendor", prefix),
    label: t("filters.vendor"),
    type: "multiple" as const,
    options: (data?.vendor || []).map((vendor) => ({
      label: vendor,
      value: vendor
    })),
    helper: [
      {
        label: t(helperKeyMultipleValues, { label: t("filters.vendor") }),
        description: t("filters.helpers.equals")
      }
    ] as [HelperFilter]
  }
  const platform = {
    key: getKey("platform", prefix),
    label: t("filters.platform"),
    type: "multiple" as const,
    options: (data?.platform || []).map((platform) => ({
      label: platform,
      value: platform
    })),
    helper: [
      {
        label: t(helperKeyMultipleValues, { label: t("filters.platform") }),
        description: t("filters.helpers.equals")
      }
    ] as [HelperFilter]
  }
  const running = {
    key: getKey("running", prefix),
    label: t("filters.running"),
    type: "multiple" as const,
    options: getYesNoOptions(t),
    helper: [
      {
        label: t(helperKeyMultipleValues, { label: t("filters.running") }),
        description: t("filters.helpers.equals")
      }
    ] as [HelperFilter]
  }

  return {
    usedByAgent,
    vendor,
    platform,
    running,
    all: [
      getEnvironmentFilter(data?.environment, { prefix, t }),
      usedByAgent,
      vendor,
      platform,
      running
    ]
  }
}

export const getMcpServersAiFilters = (
  response: KosmosApi.Response<Record<string, string[]>>,
  props: { t: TFunction }
): {
  [key in string]: OptionFilter | OptionFilter[]
} => {
  const { data } = response
  const { t } = props
  const prefix = PREFIX_BY_TYPE.mcp_server
  const locality = {
    key: getKey("locality", prefix),
    label: t("filters.locality"),
    type: "multiple" as const,
    options: (data?.locality || []).map((value) => ({
      label: MCP_LOCALITY_LABELS[value as McpLocalityAi] || value,
      value
    })),
    helper: [
      {
        label: t(helperKeyMultipleValues, { label: t("filters.locality") }),
        description: t("filters.helpers.equals")
      }
    ] as [HelperFilter]
  }

  // The search matches a typed key against the lowercase label; the display label keeps the case.
  const name = getInputFilter(getKey("name", prefix), t, {
    label: "filters.mcpServer"
  })

  const usedByAgent = getUsedByAgentFilter(data?.usedByAgent, { prefix, t })

  return {
    locality,
    usedByAgent,
    all: [
      { ...name, label: name.label.toLowerCase(), displayLabel: name.label },
      getEnvironmentFilter(data?.environment, { prefix, t }),
      usedByAgent,
      locality
    ]
  }
}

export const getKnowledgeSourcesAiFilters = (
  response: KosmosApi.Response<Record<string, string[]>>,
  props: { t: TFunction }
): {
  [key in string]: OptionFilter | OptionFilter[]
} => {
  const { data } = response
  const { t } = props
  const prefix = PREFIX_BY_TYPE.knowledge_source
  const usedByAgent = getUsedByAgentFilter(data?.usedByAgent, { prefix, t })
  const kind = {
    key: getKey("kind", prefix),
    label: t("filters.kind"),
    type: "multiple" as const,
    options: (data?.kind || []).map((value) => ({
      label: t(`inventory.ai.knowledgeSources.kinds.${value}`),
      value
    })),
    helper: [
      {
        label: t(helperKeyMultipleValues, { label: t("filters.kind") }),
        description: t("filters.helpers.equals")
      }
    ] as [HelperFilter]
  }
  const runtime = {
    key: getKey("runtime", prefix),
    label: t("filters.runtime"),
    type: "single" as const,
    options: getYesNoOptions(t)
  }
  const name = getInputFilter(getKey("name", prefix), t, {
    label: "filters.knowledgeSource"
  })

  return {
    usedByAgent,
    kind,
    runtime,
    all: [
      { ...name, label: name.label.toLowerCase(), displayLabel: name.label },
      getEnvironmentFilter(data?.environment, { prefix, t }),
      kind,
      usedByAgent,
      runtime
    ]
  }
}

export const getShowMoreData = (
  items: string[] | null | undefined,
  props: { id: string; count?: number }
) => {
  const { id, count = 1 } = props

  if (!Array.isArray(items) || items.length === 0)
    return { id, label: "", items: [] }

  return {
    id,
    items,
    label: items.slice(0, count).join(", "),
    count: items.length - count
  }
}

// An agent named in a column, opening its drawer
const getAgentDrawerItem = ({ id, name }: Pick<AgentAiApi, "id" | "name">) => ({
  name,
  drawerEntityId: id,
  drawerId: AGENT_AI_DISCOVERY_DRAWER_ID
})

// The agents using a component, each opening its agent's drawer
export const getUsedByAgentsShowMoreData = (
  agents: Pick<AgentAiApi, "id" | "name">[] | null | undefined,
  id: string
) => ({
  ...getShowMoreData(
    (agents || []).map(({ name }) => name),
    { id }
  ),
  items: (agents || []).map(getAgentDrawerItem)
})

// Required-access flags shown as tool insights (demo granularity).
const getRequiredAccessInsights = (tool: ToolAiAPI) => {
  const access = getRequiredAccess(tool)

  return {
    networkAccess: access.network,
    fileSystemAccess: access["file-system"],
    executionAccess: access.execution
  }
}

export const getToolsAi =
  (props: CommonProps) => (response: ToolsAiResponse) => {
    const { t } = props
    const { data = [], ...rest } = response

    return {
      data: data.map((tool) => {
        return {
          key: tool.id,
          usedByAgentsShowMore: getUsedByAgentsShowMoreData(
            tool.usedByAgents,
            `${tool.id}_usedByAgents`
          ),
          dataCategoriesShowMore: getShowMoreData(
            tool.dataCategory.map((category) =>
              t(`inventory.ai.categories.${category}`)
            ),
            {
              id: `${tool.id}_dataCategories`
            }
          ),
          apps: getApplications(tool.relatedApplications),
          ...tool,
          insights: {
            id: `insight_${tool.id}`,
            ...tool.insights,
            ...getRequiredAccessInsights(tool)
          }
        }
      }),
      ...rest
    }
  }

export const getModelsAi = (_: CommonProps) => (response: ModelsAiResponse) => {
  const { data = [], ...rest } = response

  return {
    data: data.map((model) => {
      return {
        key: model.id,
        usedByAgentsShowMore: getUsedByAgentsShowMoreData(
          model.usedByAgents,
          `${model.id}_usedByAgents`
        ),
        running: model.runtime ? "Running" : "Not running",
        ...model
      }
    }),
    ...rest
  }
}

const describeEndpoint = (endpoint: McpEndpointAiApi) => {
  const target = endpoint.url || endpoint.host || endpoint.command
  if (!target) return endpoint.transport
  return endpoint.transport ? `${endpoint.transport}: ${target}` : target
}

export const getMcpServerAgents = (
  server: Pick<McpServerAiApi, "usedByAgents" | "callingAgentIds">
) => {
  const calling = new Set(server.callingAgentIds || [])

  return server.usedByAgents.map((agent) => ({
    ...getAgentDrawerItem(agent),
    id: agent.id,
    calls: calling.has(agent.id)
  }))
}

export const getMcpServersAi =
  (_: CommonProps) => (response: McpServersAiResponse) => {
    const { data = [], ...rest } = response

    return {
      data: data.map((server) => {
        const { id, locality, tools, endpoints, insights } = server

        return {
          key: id,
          localityLabel: locality ? MCP_LOCALITY_LABELS[locality] : "",
          // A platform's agents each hold their own row of a server's tool
          toolsShowMore: getShowMoreData(
            [...new Set((tools || []).map((tool) => tool.name))],
            { id: `${id}_tools` }
          ),
          endpointsShowMore: getShowMoreData(
            (endpoints || []).map(describeEndpoint).filter(Boolean),
            { id: `${id}_endpoints` }
          ),
          apps: getApplications(server.relatedApplications),
          relatedAgents: getMcpServerAgents(server),
          usedByAgentsShowMore: getUsedByAgentsShowMoreData(
            server.usedByAgents,
            `${id}_usedByAgents`
          ),
          ...server,
          ...getProjectSource(server.project),
          platformDetails: {
            platformKey: getInventoryPlatformKey(server.platform)
          },
          insights: { id: `insight_${id}`, ...insights }
        }
      }),
      ...rest
    }
  }

const MCP_REMOTE_TRANSPORTS = new Set(["http", "sse", "streamable_http"])

// A client's locality follows its transport: remote over http, sse or
// streamable_http, local over stdio; "" when the transport is unknown
export const getMcpClientLocality = (transport: string): McpLocalityAi | "" => {
  if (MCP_REMOTE_TRANSPORTS.has(transport)) return "remote"
  if (transport === "stdio") return "local"

  return ""
}

// The MCP clients of one agent, as the agent's MCP clients tab lists them.
// Related tools are the ones the API reports for the client, not filtered here.
// The source is resolved as the MCP servers table resolves its project.
export const getAgentMcpClientsAi = (response: McpClientsAiResponse) => {
  const { data = [], ...rest } = response

  return {
    data: data.map((client) => {
      const locality = getMcpClientLocality(client.transport)

      return {
        key: client.id,
        id: client.id,
        name: client.name,
        localityLabel: locality ? MCP_LOCALITY_LABELS[locality] : "",
        endpointUrl: client.endpoint || "",
        relatedToolsShowMore: getShowMoreData(
          [...new Set((client.relatedTools || []).map((tool) => tool.name))],
          { id: `${client.id}_relatedTools` }
        ),
        ...getProjectSource(client.source),
        platformDetails: {
          platformKey: getInventoryPlatformKey(client.platform)
        }
      }
    }),
    ...rest
  }
}

// The listing's total is the agent's client count: the tab is greyed out at zero
export const getMcpClientsCountAi = (response: McpClientsAiResponse) =>
  response.metadata?.total

// The clients the hierarchy graph draws: each client with the tools it carries
export const getMcpClientsGraphAi = (response: McpClientsAiResponse) =>
  (response.data || []).map(({ id, name, relatedTools }) => ({
    id,
    name,
    tools: relatedTools || []
  }))

export const getKnowledgeSourcesAi =
  (props: CommonProps) => (response: KnowledgeSourcesAiResponse) => {
    const { t } = props
    const { data = [], ...rest } = response

    return {
      data: data.map((source) => {
        const { id, kind, identifier, endpoint, insights } = source

        return {
          key: id,
          kindLabel: kind
            ? t(`inventory.ai.knowledgeSources.kinds.${kind}`)
            : "",
          location: identifier || endpoint,
          usedByAgentsShowMore: getUsedByAgentsShowMoreData(
            source.usedByAgents,
            `${id}_usedByAgents`
          ),
          apps: getApplications(source.relatedApplications),
          ...source,
          insights: { id: `insight_${id}`, ...insights }
        }
      }),
      ...rest
    }
  }

export const getWorkflowsAi =
  (_: CommonProps) => (response: WorkflowsAiResponse) => {
    const { data = [], ...rest } = response

    return {
      data: data.map((workflow) => {
        const { id, insights, project, ...restWorkflow } = workflow
        // A workflow's own type (sequential, …) stays: not its project's
        const { projectName, resources, sourceControl } =
          getProjectSource(project)

        return {
          key: id,
          projectName,
          resources,
          sourceControl,
          apps: getApplications(workflow.relatedApplications),
          severity: getIssuesCountMapOrderedSeverity(workflow.riskSummary),
          insights: { id: `insight_${id}`, ...insights },
          id,
          ...restWorkflow
        }
      }),
      ...rest
    }
  }

// A workflow's data flow in the shape the data flow graph reads: its tools and
// code steps as the category's tools
const getWorkflowFlow = (categories: WorkflowDataFlowAiApi[] | undefined) =>
  (categories ?? []).map(
    ({ category, displayName, description, total, running, components }) => ({
      category,
      displayName,
      description,
      totalTools: total,
      runningTools: running,
      tools: (components ?? []).map(
        ({
          id,
          name,
          type,
          runtime,
          toolType,
          packageName,
          mcpServerName
        }) => ({
          id,
          name,
          type: type === "code_step" ? "code_step" : toolType,
          insights: { runtime },
          packageName,
          mcpClient: mcpServerName,
          // A component takes its flow's category, so it takes the category's color
          dataCategory: [category]
        })
      )
    })
  )

export const getWorkflowAi = (response: WorkflowAiResponse) => {
  const { data, ...rest } = response
  const { dataSources, dataSinks, insights, ...restData } =
    data || ({} as WorkflowAiByIdApi)

  return {
    ...rest,
    data: {
      ...restData,
      insights,
      apps: getApplications(restData.relatedApplications ?? []),
      dataSources: getWorkflowFlow(dataSources),
      dataSinks: getWorkflowFlow(dataSinks)
    }
  }
}

// A workflow's steps as rows keyed by id, each with the show-more column it builds
const getWorkflowStepsAi =
  <T extends { id: string; insights: { runtime: boolean } }>(
    getShowMore: (step: T) => Record<string, unknown>,
    // The insights a kind shows on top of the ones the step carries
    getInsights: (step: T) => Record<string, boolean> = () => ({})
  ) =>
  (response: KosmosApi.Response<T[]>) => {
    const { data = [], ...rest } = response

    return {
      data: data.map((step) => ({
        key: step.id,
        ...getShowMore(step),
        ...step,
        insights: {
          id: `insight_${step.id}`,
          ...step.insights,
          ...getInsights(step)
        }
      })),
      ...rest
    }
  }

export const getModelCallsAi = (_: CommonProps) =>
  getWorkflowStepsAi<ModelCallAiApi>((call) => ({
    modelShowMore: getShowMoreData(
      (call.models || []).map((model) => model.name),
      { id: `${call.id}_models` }
    )
  }))

export const getCodeStepsAi = ({ t }: CommonProps) =>
  getWorkflowStepsAi<CodeStepAiApi>(
    (step) => ({
      dataCategoriesShowMore: getShowMoreData(
        (step.dataCategory || []).map((category) =>
          t(`inventory.ai.categories.${category}`)
        ),
        {
          id: `${step.id}_categories`
        }
      )
    }),
    // A code step's guard is its hasGuardrails insight, as an agent's is
    (step) => ({ hasGuardrails: !!step.insights.isGuard })
  )

export const getStatisticsAi = (response: StatisticsAiResponse) => {
  const { data, ...rest } = response
  const { topAgentsByRisk, ...restData } = data || {}

  return {
    data: {
      topAgentsByRisk: (topAgentsByRisk || []).map((agent) => {
        return {
          severity: getIssuesCountMapOrderedSeverity(agent.riskSummary),
          ...agent
        }
      }),
      ...restData
    },
    ...rest
  }
}
